import { z } from "zod";
import { EnvironmentViewSchema } from "./environment-view.js";
import { RubricDimensionSchema } from "./primitives.js";
import { ScenePlanSchema } from "./story-event.js";
/**
 * THE CASEBOOK (2026-09-24, Andre): a finished engagement read back as a book.
 * It looks like the play screen with the composer gone, and it ends with the
 * learner's recommendation, the cross-examination, and the review: what was
 * true, where it turned, what they would have found, how the work was read,
 * and the coach's note on this one engagement.
 *
 * Everything here is already in the event log, the debrief and the assessment
 * record except the coach's note, which is written once per finished
 * engagement and kept (`coach_engagement_notes`).
 *
 * LAW 2: the book stays in words. Readings are words (low, middling, strong,
 * not exercised), never scores; the mechanics stay in the tape (the existing
 * debrief), one press away.
 */
/** How the engagement ended, as the debrief already records it. */
export const CasebookEndingSchema = z.enum(["presented", "presented_at_deadline", "time_expired"]);
/** One turn of the story, with the room as it was staged on that turn. */
export const CasebookTurnSchema = z.strictObject({
    turn: z.number().int().min(0),
    /**
     * The chapter of the in-game day this turn ended on: the coded slug line of the moment that date's first turn ended at,
     * "Monday, April 6, 8:40 a.m." (2026-10-29; the date since ruling 2 on PR #167), the same value GET /sessions/:id/turns sends; never an authored
     * beat title. A new slug starts a chapter.
     */
    chapter: z.string().nullable(),
    /** The learner's own message; null for turn 0 and engine-initiated turns. */
    learnerText: z.string().nullable(),
    narration: z.array(z.string()),
    /** Papers that arrived on this turn (title only: the desk opens them read-only). */
    documents: z.array(z.strictObject({ docId: z.string(), kind: z.string(), title: z.string() })),
    npcTells: z.array(z.string()),
    /**
     * The scene as the event log stood at the end of this turn (a pure fold, as in play), except that a
     * turn crossing into a new day keeps the wall clock, the flip calendar (with its window light) and
     * the calendar's caption in `description` it began under, so it agrees with the heading of the day
     * it is printed under (Andre, ruling 1 on PR #167, 2026-10-29; apps/api casebook/story.ts). Live
     * play's room is always the present.
     */
    environment: EnvironmentViewSchema.nullable(),
    /** The staging compiled for this turn, if one was. */
    plan: ScenePlanSchema.nullable(),
});
export const CasebookReadingWordSchema = z.enum(["low", "middling", "strong", "not exercised"]);
export const CasebookResponseSchema = z.strictObject({
    sessionId: z.string().min(1),
    caseSlug: z.string().min(1),
    client: z.string().min(1),
    industry: z.string().min(1),
    engagementQuestion: z.string().min(1),
    /** When the engagement closed (its debrief was written). */
    completedAt: z.iso.datetime(),
    ending: CasebookEndingSchema,
    /** The threads still open when the deadline came, for the two deadline endings; empty otherwise. */
    openThreads: z.array(z.string()),
    turns: z.array(CasebookTurnSchema),
    /**
     * The room the story's last turn left, as the event log stood at its end: where the closing chapters
     * (how it ended, the recommendation, the examination) are drawn, the room the recommendation was
     * made in. Never held back to the moment a move began: no day heading stands over those pages, so
     * ruling 1 on PR #167 does not reach them, and when the last story turn crosses into a new day its
     * own entry in `turns` keeps the day before while this is the next. Null with no story turns or no
     * room (2026-10-29).
     */
    closingEnvironment: EnvironmentViewSchema.nullable(),
    /** The recommendation exactly as submitted, paragraph breaks kept; null when none was presented. */
    recommendation: z.string().nullable(),
    /** The cross-examination: who asked, and every exchange in order. Empty when there was none. */
    crossExam: z.strictObject({
        examiner: z.string().min(1),
        exchanges: z.array(z.strictObject({ question: z.string(), answer: z.string() })),
    }),
    review: z.strictObject({
        /** "What was actually true." */
        truth: z.strictObject({ mechanism: z.string(), whyNonObvious: z.string() }),
        /** "Where it turned.": the authored moments of highest leverage. */
        turned: z.array(z.string()),
        /** "What you would have found.": the authored counterfactuals. */
        wouldHaveFound: z.array(z.strictObject({ thread: z.string(), reveal: z.string() })),
        /** "How the work was read.": the Assessor's reading, in words. */
        reading: z.strictObject({
            /** "This case was designed to test your ...", in the Assessor's words; null on older records. */
            caseFocus: z.string().nullable(),
            /** The Assessor's own account of process against outcome, for the learner; null when none was recorded. */
            overall: z.string().nullable(),
            skills: z.array(z.strictObject({
                dimension: RubricDimensionSchema,
                word: CasebookReadingWordSchema,
                /** The Assessor's recorded reason for a reading below strong, when it gave one. */
                reason: z.string().nullable(),
            })),
        }),
        /** "Your coach, on this engagement.": written once and kept; null while it is still being written or if the coach could not be reached. */
        coachNote: z
            .strictObject({
            text: z.string().min(1),
            writtenAt: z.iso.datetime(),
            /** True when the note was written on a later day than the engagement closed, so the page says "added". */
            addedLater: z.boolean(),
        })
            .nullable(),
    }),
});
/** A finished engagement on the library's Completed box, newest first. */
export const LibraryCompletedEngagementSchema = z.strictObject({
    sessionId: z.string().min(1),
    caseSlug: z.string().min(1),
    completedAt: z.iso.datetime(),
    ending: CasebookEndingSchema,
});
//# sourceMappingURL=casebook.js.map